import { useEffect, useRef, useState } from 'react';
import type { GlobePhase } from '@colescomex/globe-runtime';
import { useStage } from '../stage/Stage';
import { useLocale } from '../i18n/locale';
import { deviceProfile, type DeviceProfile } from './narrative';
import './globe.css';

/**
 * Fixed globe layer behind the HOME. Mounted after first paint (the headline is the LCP element)
 * and remounted only when the device profile changes (phone, tablet portrait, desktop framing).
 * Reduced motion / no WebGL / context loss → static rendered poster of the same globe.
 */
export function GlobeStage() {
  const { globe } = useStage(); const { locale } = useLocale();
  const container = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<GlobePhase>('UNMOUNTED');

  useEffect(() => globe.subscribe(snapshot => setPhase(snapshot.phase)), [globe]);
  useEffect(() => {
    const target = container.current; if (!target) return;
    let disposed = false; let profile: DeviceProfile = deviceProfile(window.innerWidth, window.innerHeight);
    const start = () => { if (!disposed) void globe.mount(target, profile, locale).catch(() => setPhase('FALLBACK')); };
    const idleApi = typeof window.requestIdleCallback === 'function';
    const idle = idleApi ? window.requestIdleCallback(start, { timeout: 1200 }) : globalThis.setTimeout(start, 400);
    const onResize = () => { const next = deviceProfile(window.innerWidth, window.innerHeight); if (next !== profile) { profile = next; globe.destroy(); start(); } };
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      disposed = true; window.removeEventListener('resize', onResize);
      if (idleApi) window.cancelIdleCallback(idle as number); else globalThis.clearTimeout(idle);
      globe.destroy();
    };
  }, [globe, locale]);

  const poster = phase === 'STATIC' || phase === 'FALLBACK';
  return (
    <div className="globe-layer" data-phase={phase} aria-hidden="true">
      <div ref={container} className="globe-layer__canvas" />
      {poster && <img className="globe-layer__poster" src="/media/globe-static.webp" alt="" width={1600} height={1600} decoding="async" />}
    </div>
  );
}
